/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import { Diagnostics } from '@tivocorp/tivo-webapp-loggers';
import { ContentType } from 'client-code/grid-guide';

import { DeviceModel } from '../../../core/device';
import { Channel, OneLineGuideCell } from '../../../core/entities';
import { GuideType, InteractiveComponents } from '../../../core/enums';
import { ServicesClientImpl } from '../../../core/interactors';
import { Log } from '../../../core/logging';
import { CursorManager } from '../../../core/navigation';
import { NetworkManager } from '../../../core/network/NetworkManager';
import { PlaybackContentType } from '../../../core/playback/enums';
import { SharedPrefConstants } from '../../../core/storage';
import { TOfferPreview, TOfferPreviewCustomData } from '../../../core/types';
import { DateTime } from '../../../core/utils';
import { VideoPlaybackController } from '../../../core/videoplayer/VideoPlaybackController';
import { onLongKeyOkEmptyGuide } from '../../guide/emptyguide/emptyguide.navigation.reducers';
import { showChannelOptionsOverlay } from '../../overlay/channeloptionsoverlay/channeloptionsoverlay.reducers';
import { showPCPinOverlayWithProgramRating } from '../../overlay/pcpinoverlay/pcpinoverlay.reducers';
import { showRecordOverlay } from '../../overlay/recordoverlay/recordoverlay.reducers';
import { triggerPlaybackOnPinVerify } from '../../videoplayer/videoplayer.reducers';
import { showTrickPlayMenu } from '../infobanner/infobanner.reducers';
import {
    setOneLineGuideAreChannelsSelectedAction,
    setOneLineGuideCellSelectedAction,
    setOneLineGuideHorizontalScrollAction,
    setOneLineGuideIsFutureModeAction,
    setOneLineGuideSelectedChannelAction,
    setOneLineGuideTimeStamp,
    setOneLineGuideTurboScrollAction,
} from './onelineguide.actions';
import {
    ONE_LINE_GUIDE_CHANNEL_SLICE_SIZE,
    ONE_LINE_GUIDE_FETCH_ASSETS_NUMBER,
    TAG,
} from './onelineguide.constants';
import {
    getMetadataForChannel,
    getOneLineGuideChannels,
    getOneLineGuideFutureThreshold,
    getOneLineGuidePastThreshold,
    getOneLineGuideRowMetadata,
    getOneLineGuideSingleChannel,
    hideOneLineGuide,
    showOneLineGuide,
    updateOneLineGuideInactivityTimeout,
} from './onelineguide.reducers';

const DEBOUNCE_VALUE = 300; // ms
let showsDebounceTimeout: NodeJS.Timeout;

export const onKeyUpOneLineGuide =
    (getMetadata = true, numberOfChannels: number = 1) =>
    (dispatch: Function, getState: Function) => {
        const {
            channels,
            selectedChannel,
            showOneLineGuide: isOneLineGuideShown,
            isEmpty,
        } = getState().oneLineGuide;
        if (!isOneLineGuideShown) {
            /*
             * There are moments when the screen task controller
             * is set to ONE_LINE_GUIDE even if it is not opened.
             * This usually happens when there was a streaming
             * error caused by poor internet connection, because
             * when the internet is stable again, the screen task
             * is set to the last one before the error.
             */
            dispatch(showOneLineGuide());
            return;
        }

        if (isEmpty || channels.length === 1) return;

        const currentChannelIndex = channels.findIndex(
            (channel) => channel.channelId === selectedChannel.channelId,
        );

        const navigationIncrement =
            channels.length < ONE_LINE_GUIDE_CHANNEL_SLICE_SIZE ? 1 : numberOfChannels;

        let nextChannelIndex = currentChannelIndex - navigationIncrement;
        if (nextChannelIndex < 0) {
            nextChannelIndex = channels.length - Math.abs(nextChannelIndex);
        }
        dispatch(setOneLineGuideSelectedChannelAction(channels[nextChannelIndex]));
        dispatch(getOneLineGuideChannels(channels[nextChannelIndex]));
        if (getMetadata) {
            dispatch(debouncedMetadataRequest(channels[nextChannelIndex]));
        }
        dispatch(updateOneLineGuideInactivityTimeout());
    };

export const onLongKeyUpOneLineGuide = () => (dispatch: Function) => {
    dispatch(setOneLineGuideTurboScrollAction(true, InteractiveComponents.NORTH));
};

export const onKeyDownOneLineGuide =
    (getMetadata = true, numberOfChannels: number = 1) =>
    (dispatch: Function, getState: Function) => {
        const { channels, selectedChannel, isEmpty } = getState().oneLineGuide;
        const currentChannelIndex = channels.findIndex(
            (channel) => channel.channelId === selectedChannel.channelId,
        );

        if (isEmpty || channels.length === 1) return;

        const navigationIncrement =
            channels.length < ONE_LINE_GUIDE_CHANNEL_SLICE_SIZE ? 1 : numberOfChannels;

        const nextChannelIndex = (currentChannelIndex + navigationIncrement) % channels.length;
        dispatch(setOneLineGuideSelectedChannelAction(channels[nextChannelIndex]));
        dispatch(getOneLineGuideChannels(channels[nextChannelIndex]));
        if (getMetadata) {
            dispatch(debouncedMetadataRequest(channels[nextChannelIndex]));
        }
        dispatch(updateOneLineGuideInactivityTimeout());
    };

export const onLongKeyDownOneLineGuide = () => (dispatch: Function) => {
    dispatch(setOneLineGuideTurboScrollAction(true, InteractiveComponents.SOUTH));
};

export const onKeyLeftOneLineGuide = () => (dispatch: Function, getState: Function) => {
    const {
        selectedChannel,
        cellList,
        currentSelectedCell,
        currentAiringCell,
        isContentFetched,
        areChannelsSelected,
        isFutureMode,
        error,
    } = getState().oneLineGuide;

    // This is the case when the OLG is opened and the first request fails.
    // We ensure that the user can still navigate to the channel list.
    if (isServiceDownOnOpen(error, cellList, currentSelectedCell)) {
        if (isFutureMode) {
            dispatch(setOneLineGuideAreChannelsSelectedAction(true));
        } else {
            dispatch(setOneLineGuideAreChannelsSelectedAction(false));
        }
        return;
    }

    if (
        !isHorizontalKeyActionAvailableOLG(
            dispatch,
            cellList,
            currentSelectedCell,
            currentAiringCell,
            'arrowLeftOneLineGuide',
        )
    ) {
        return;
    }
    const currentSelectedCellIndex = cellList.indexOf(currentSelectedCell);
    const currentAiringCellIndex = cellList.indexOf(currentAiringCell);

    // for past olg, go back to strip from channel list
    if (currentSelectedCellIndex + 1 === currentAiringCellIndex && areChannelsSelected) {
        dispatch(setOneLineGuideAreChannelsSelectedAction(false));
        dispatch(setOneLineGuideIsFutureModeAction(false));
        return;
    }

    if (currentSelectedCellIndex < currentAiringCellIndex && isFutureMode) {
        dispatch(setOneLineGuideAreChannelsSelectedAction(true));
        return;
    }

    const isOpenedInDisconnectState = error && cellList.length === 0;

    // for future olg, decide whether to go in past or give control to channel list
    if (currentSelectedCellIndex === currentAiringCellIndex) {
        if (areChannelsSelected && !isOpenedInDisconnectState) {
            dispatch(setOneLineGuideIsFutureModeAction(false));
            dispatch(setOneLineGuideAreChannelsSelectedAction(false));
        } else {
            dispatch(setOneLineGuideAreChannelsSelectedAction(true));
            return;
        }
    }

    if (currentSelectedCellIndex - 1 < 0) return;
    dispatch(setOneLineGuideCellSelectedAction(cellList[currentSelectedCellIndex - 1]));

    let newTimeStamp: DateTime;
    if (currentAiringCell === cellList[currentSelectedCellIndex - 1]) {
        newTimeStamp = new DateTime();
    } else if (
        cellList[currentSelectedCellIndex - 1].startTime.getTime() < getOneLineGuidePastThreshold()
    ) {
        newTimeStamp = new DateTime(getOneLineGuidePastThreshold());
    } else {
        newTimeStamp = cellList[currentSelectedCellIndex - 1].startTime;
    }
    dispatch(setOneLineGuideTimeStamp(newTimeStamp));

    const firstCellTime: DateTime = cellList[0].startTime.ceilByMinutes(30);

    if (firstCellTime.getTime() < getOneLineGuidePastThreshold()) {
        return;
    }

    if (currentSelectedCellIndex > ONE_LINE_GUIDE_FETCH_ASSETS_NUMBER) return;

    if (isContentFetched) return;

    dispatch(
        getOneLineGuideRowMetadata(
            selectedChannel,
            firstCellTime,
            ONE_LINE_GUIDE_FETCH_ASSETS_NUMBER,
            true,
        ),
    );
};

export const onKeyRightOneLineGuide = () => (dispatch: Function, getState: Function) => {
    const {
        selectedChannel,
        cellList,
        currentSelectedCell,
        currentAiringCell,
        isContentFetched,
        areChannelsSelected,
        isFutureMode,
        error,
    } = getState().oneLineGuide;

    // This is the case when the OLG is opened and the first request fails.
    // We ensure that the user can still navigate to the right dummy channel cell.
    if (isServiceDownOnOpen(error, cellList, currentSelectedCell)) {
        if (isFutureMode) {
            dispatch(setOneLineGuideAreChannelsSelectedAction(false));
        } else {
            dispatch(setOneLineGuideAreChannelsSelectedAction(true));
        }
        return;
    }

    if (
        !isHorizontalKeyActionAvailableOLG(
            dispatch,
            cellList,
            currentSelectedCell,
            currentAiringCell,
            'arrowRightOneLineGuide',
        )
    ) {
        return;
    }

    const currentAiringCellIndex = cellList.indexOf(currentAiringCell);
    const currentSelectedCellIndex = cellList.indexOf(currentSelectedCell);

    // for future olg, go back to strip from channel list
    if (currentSelectedCellIndex === currentAiringCellIndex && areChannelsSelected) {
        dispatch(setOneLineGuideAreChannelsSelectedAction(false));
        return;
    }

    // for past olg, decide whether to go in future or give control to channel list
    if (!isFutureMode && currentSelectedCellIndex + 1 === currentAiringCellIndex) {
        if (areChannelsSelected) {
            dispatch(setOneLineGuideIsFutureModeAction(true));
            dispatch(setOneLineGuideAreChannelsSelectedAction(false));
        } else {
            dispatch(setOneLineGuideAreChannelsSelectedAction(true));
            return;
        }
    }

    if (currentSelectedCellIndex + 1 >= cellList.length) return;
    dispatch(setOneLineGuideCellSelectedAction(cellList[currentSelectedCellIndex + 1]));

    let newTimeStamp: DateTime;
    if (currentAiringCell === cellList[currentSelectedCellIndex + 1]) {
        newTimeStamp = new DateTime();
    } else if (
        cellList[currentSelectedCellIndex + 1].startTime.getTime() >
        getOneLineGuideFutureThreshold()
    ) {
        newTimeStamp = new DateTime(getOneLineGuideFutureThreshold());
    } else {
        newTimeStamp = cellList[currentSelectedCellIndex + 1].startTime;
    }
    dispatch(setOneLineGuideTimeStamp(newTimeStamp));

    const lastCellTime: DateTime = cellList[cellList.length - 1].startTime.floorByMinutes(30);

    if (lastCellTime.getTime() > getOneLineGuideFutureThreshold()) {
        return;
    }

    const cellCountUntilEndOfList: number = cellList.length - currentSelectedCellIndex;
    if (cellCountUntilEndOfList > ONE_LINE_GUIDE_FETCH_ASSETS_NUMBER) return;

    if (isContentFetched) return;

    dispatch(
        getOneLineGuideRowMetadata(
            selectedChannel,
            lastCellTime,
            ONE_LINE_GUIDE_FETCH_ASSETS_NUMBER,
            false,
        ),
    );
};

export const onKeyOkOneLineGuide =
    (isLongPress = false) =>
    async (dispatch: Function, getState: Function) => {
        const {
            areChannelsSelected,
            selectedChannel,
            channelsType,
            currentSelectedCell,
            currentAiringCell,
            offerPreview: offerPreviewPromise,
            isEmpty,
            error,
            cellList,
        } = getState().oneLineGuide;

        // This is the case when the OLG is opened and the first request fails.
        // We ensure that the user can still open the channel options overlay.
        if (
            isServiceDownOnOpen(error, cellList, currentSelectedCell) &&
            areChannelsSelected &&
            selectedChannel?.channelId
        ) {
            dispatch(hideOneLineGuide());
            const currentChannel = await getOneLineGuideSingleChannel(
                selectedChannel.channelId,
                channelsType,
            );
            dispatch(
                showChannelOptionsOverlay(
                    currentChannel ?? selectedChannel,
                    GuideType.ONE_LINE_GUIDE,
                ),
            );
            return;
        }

        if (isEmpty || currentSelectedCell === null) return;

        const offerPreview = await getCurrentPreview(offerPreviewPromise, currentSelectedCell);

        dispatch(hideOneLineGuide());
        if (areChannelsSelected && !NetworkManager.isNetworkDisconnected()) {
            updateRecordingSharedPreferenceBeforeOpeningOverlay(currentSelectedCell);
            const currentChannel = await getOneLineGuideSingleChannel(
                selectedChannel.channelId,
                channelsType,
            );
            dispatch(
                showChannelOptionsOverlay(
                    currentChannel ?? selectedChannel,
                    GuideType.ONE_LINE_GUIDE,
                ),
            );
        } else if (
            currentSelectedCell === currentAiringCell &&
            !isLongPress &&
            !NetworkManager.isNetworkDisconnected()
        ) {
            VideoPlaybackController.getInstance().startPlayback({
                channel: selectedChannel,
                playbackContentType: PlaybackContentType.LIVE,
            });
            dispatch(showTrickPlayMenu());
        } else if (!NetworkManager.isNetworkDisconnected()) {
            const guideCell = currentSelectedCell;

            const isExplicitlyAdult = offerPreview.contentPreview?.isAdult ?? false;
            const isContentPCViolated = new ServicesClientImpl().checkPCViolations({
                offerPreview,
            });
            const invokeRecordOverlay = () => async () => {
                await dispatch(
                    showRecordOverlay({
                        channel: selectedChannel,
                        guideCell,
                        offerPreview,
                        showMetaData: false,
                    }),
                );
                isExplicitlyAdult && dispatch(triggerPlaybackOnPinVerify());
            };
            updateRecordingSharedPreferenceBeforeOpeningOverlay(currentSelectedCell);
            if (isExplicitlyAdult && isContentPCViolated) {
                dispatch(showPCPinOverlayWithProgramRating(invokeRecordOverlay, isExplicitlyAdult));
            } else {
                dispatch(invokeRecordOverlay());
            }
        }
    };

export const onLongKeyOkOneLineGuide = () => (dispatch: Function, getState: Function) => {
    const { inputFactory, oneLineGuide } = getState();
    const { value } = inputFactory;
    const { isEmpty } = oneLineGuide;

    if (isEmpty) {
        dispatch(onLongKeyOkEmptyGuide(GuideType.ONE_LINE_GUIDE));
        return;
    }

    if (CursorManager.isCursorAppeared()) {
        switch (value) {
            case InteractiveComponents.WEST:
                dispatch(onKeyLeftOneLineGuide());
                break;
            case InteractiveComponents.EAST:
                dispatch(onKeyRightOneLineGuide());
                break;
            default:
                dispatch(onKeyOkOneLineGuide(true));
                break;
        }
    } else {
        dispatch(onKeyOkOneLineGuide(true));
    }
};

export const onMouseWheelOneLineGuide = (wheelDeltaY: number) => (dispatch: Function) => {
    if (wheelDeltaY < 0) dispatch(onKeyUpOneLineGuide());
    if (wheelDeltaY > 0) dispatch(onKeyDownOneLineGuide());
};

export const onMouseClickOneLineGuide = () => (dispatch: Function, getState: Function) => {
    const { inputFactory } = getState();
    const { value } = inputFactory;

    switch (value) {
        case InteractiveComponents.NORTH:
            dispatch(onKeyUpOneLineGuide());
            break;
        case InteractiveComponents.SOUTH:
            dispatch(onKeyDownOneLineGuide());
            break;
        case InteractiveComponents.WEST:
            dispatch(onKeyLeftOneLineGuide());
            break;
        case InteractiveComponents.EAST:
            dispatch(onKeyRightOneLineGuide());
            break;
        default:
            dispatch(onKeyOkOneLineGuide());
            break;
    }
};

export const onLongClickOneLineGuide = () => (dispatch: Function, getState: Function) => {
    const { inputFactory } = getState();
    const { value } = inputFactory;

    switch (value) {
        case InteractiveComponents.NORTH:
            dispatch(setOneLineGuideTurboScrollAction(true, InteractiveComponents.NORTH));
            break;
        case InteractiveComponents.SOUTH:
            dispatch(setOneLineGuideTurboScrollAction(true, InteractiveComponents.SOUTH));
            break;
        case InteractiveComponents.WEST:
            dispatch(setOneLineGuideHorizontalScrollAction(InteractiveComponents.WEST));
            break;
        case InteractiveComponents.EAST:
            dispatch(setOneLineGuideHorizontalScrollAction(InteractiveComponents.EAST));
            break;
        default:
            dispatch(onLongKeyOkOneLineGuide());
            break;
    }
};

export const onLongClickStopOneLineGuide =
    () =>
    (dispatch: Function, getState: Function): void => {
        const { oneLineGuide } = getState();
        const { oneLineGuideTurboScroll, timeStamp, selectedChannel } = oneLineGuide;

        dispatch(setOneLineGuideHorizontalScrollAction(null));
        if (oneLineGuideTurboScroll) {
            dispatch(getMetadataForChannel(selectedChannel, timeStamp));
            dispatch(setOneLineGuideTurboScrollAction(false));
        }
    };

export const onLongKeyStopOneLineGuide =
    () =>
    (dispatch: Function, getState: Function): void => {
        const { oneLineGuide } = getState();
        const { oneLineGuideTurboScroll, timeStamp, selectedChannel } = oneLineGuide;

        if (oneLineGuideTurboScroll) {
            dispatch(getMetadataForChannel(selectedChannel, timeStamp));
            dispatch(setOneLineGuideTurboScrollAction(false));
        }
    };

const getCurrentPreview = async (
    offerPreviewPromise: Promise<TOfferPreview>,
    currentSelectedCell: OneLineGuideCell,
): Promise<TOfferPreviewCustomData> => {
    try {
        const { offerPreview } = await offerPreviewPromise;
        return offerPreview;
    } catch {
        const offerPreview: TOfferPreviewCustomData = {};
        offerPreview.contentPreview = {
            ...currentSelectedCell,
            previewType:
                currentSelectedCell.contentType === ContentType.Episode
                    ? 'EpisodePreview'
                    : 'MoviePreview',
            startTime: currentSelectedCell.startTime.toISOString(),
        };
        return offerPreview;
    }
};

const debouncedMetadataRequest =
    (nextChannel: Channel) => (dispatch: Function, getState: Function) => {
        const { timeStamp } = getState().oneLineGuide;

        if (showsDebounceTimeout) {
            clearTimeout(showsDebounceTimeout);
        }
        showsDebounceTimeout = setTimeout(() => {
            dispatch(getMetadataForChannel(nextChannel, timeStamp));
        }, DEBOUNCE_VALUE);
    };

const isHorizontalKeyActionAvailableOLG = (
    dispatch: Function,
    cellList: OneLineGuideCell[],
    currentSelectedCell: OneLineGuideCell,
    currentAiringCell: OneLineGuideCell,
    tag: 'arrowLeftOneLineGuide' | 'arrowRightOneLineGuide',
) => {
    Diagnostics.info(TAG, tag);
    Log.i(TAG, tag);

    dispatch(updateOneLineGuideInactivityTimeout());

    const currentSelectedCellIndex = cellList.indexOf(currentSelectedCell);
    const currentAiringCellIndex = cellList.indexOf(currentAiringCell);
    const isNetworkDisconnected = NetworkManager.isNetworkDisconnected();

    if (
        (currentSelectedCellIndex === -1 && currentAiringCellIndex === -1) ||
        isNetworkDisconnected
    ) {
        return false;
    }
    return true;
};

export const updateRecordingSharedPreferenceBeforeOpeningOverlay = (
    currentSelectedCell: OneLineGuideCell,
) => {
    const recordingSharedPreferenceEditor = DeviceModel.getSharedPreferences(
        SharedPrefConstants.RECORDING_ACTION_PREF_KEY,
    ).edit();

    recordingSharedPreferenceEditor.putString(
        SharedPrefConstants.RECORDING_OFFER_ID,
        currentSelectedCell.offerId,
    );

    recordingSharedPreferenceEditor.putString(
        SharedPrefConstants.RECORDING_CONTENT_ID,
        currentSelectedCell.contentId,
    );
    recordingSharedPreferenceEditor.commit();
};

const isServiceDownOnOpen = (
    error: any,
    cellList: OneLineGuideCell[],
    currentSelectedCell: OneLineGuideCell | null,
) => {
    return (
        error &&
        cellList.length === 0 &&
        currentSelectedCell === null &&
        !NetworkManager.isNetworkDisconnected()
    );
};
